/**
 * SharedFooter — v3 homepage footer.
 *
 * Three-column structured footer, reusable across the site.
 *
 * Structure:
 *   COL 1 (brand)   : larger logo + tagline
 *   COL 2 (Product) : Book a cleanup · Become a PooPartner · About ·
 *                     Pricing · Map · Social
 *   COL 3 (Legal)   : Terms · Privacy · Contact
 *   Bottom stripe   : copyright (slightly darker purple — Option A)
 *
 * Visuals (post-2026-06-22):
 *   - Entire footer sits on `var(--pp-purple)` (#574B8C). No token flip
 *     across light/dark (purple is brand-constant).
 *   - 3-col body padding: pt-10 / pb-12 (top tightened 2026-02 iter-3 —
 *     was pt-14 (56 px), dialed back ~29 % to pt-10 (40 px). Two prior
 *     iterations: pt-14 (56) → pt-20 (80) → pt-14 (56) → pt-10 (40).
 *     pt-10 chosen over pt-9 (36 px) to preserve optical balance against
 *     the dog mark, which carries more visual weight than the small-caps
 *     PRODUCT / LEGAL headings. The padding lives on an INNER wrapper
 *     div, NOT the outer `.pp-wrap`, because `.pp-wrap` declares the CSS
 *     shorthand `padding: 0 32px` which would otherwise reset
 *     `padding-top: 0` and silently override any Tailwind `pt-*` utility.
 *   - Bottom stripe: `var(--pp-purple-dark)` (#473C75) — subtle step
 *     preserves the "bottom bar" affordance without breaking unity.
 *   - All text white. Links: white at rest, --pp-orange on hover.
 *   - Tagline: white at 90% opacity (~6.16:1 → AA).
 *   - No border-t — the hard purple-on-page color change is its own
 *     separator.
 */
import React from "react";
import { Link } from "react-router-dom";

const PRODUCT_LINKS = [
  // Pre-Launch Demand Capture (2026-07-21) — secondary CTAs swept
  // to the waitlist. The `/#waitlist` target relies on the landing
  // page's anchor id (WaitlistForm renders `id="waitlist"`).
  { label: "Join the waitlist", to: "/#waitlist" },
  { label: "Become a PooPartner", to: "/signup?role=provider" },
  // Prod Hotfix Batch 2 P1-3 — dead `href="#"` sweep. All four
  // links previously routed to `"#"` (which the `FooterLink`
  // component rendered as a stub `<a href="#">`). Each now
  // resolves to its canonical public route. Drift-guard:
  // `SharedFooter.deadlinks.test.jsx`.
  { label: "About", to: "/about" },
  { label: "Pricing", to: "/pricing" },
  { label: "Map", to: "/map" },
  { label: "Social", to: "/social" },
];

const LEGAL_LINKS = [
  { label: "Terms", to: "/terms" },
  { label: "Privacy", to: "/privacy" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

/** Footer link styling — slate on white v3-surface, orange-hover on accent.
 *  WCAG verified: --pp-slate on --pp-v3-surface ≈ 9 : 1 AAA; --pp-orange-hover
 *  on --pp-v3-surface measures ≈ 3.5 : 1 (AA-large only). The link font is
 *  15 px Manrope 500 — borderline, but hover is a transient cue and the
 *  resting state is the AAA combo. */
const FOOTER_LINK_CLS =
  "block text-[15px] font-medium transition-colors py-2.5";
const FOOTER_LINK_STYLE = { color: "var(--pp-slate)", minHeight: 44 };
const FOOTER_LINK_HOVER_COLOR = "var(--pp-orange-hover)";

function FooterLink({ to, label, testId }) {
  const isStub = to === "#";
  const sharedProps = {
    className: FOOTER_LINK_CLS,
    style: FOOTER_LINK_STYLE,
    onMouseEnter: (e) => (e.currentTarget.style.color = FOOTER_LINK_HOVER_COLOR),
    onMouseLeave: (e) => (e.currentTarget.style.color = "var(--pp-slate)"),
    "data-testid": testId,
  };
  if (isStub) {
    return (
      <a href="#" {...sharedProps}>
        {label}
      </a>
    );
  }
  return (
    <Link to={to} {...sharedProps}>
      {label}
    </Link>
  );
}

export default function SharedFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      style={{ background: "var(--pp-v3-surface)", color: "var(--pp-slate)" }}
      data-testid="shared-footer"
    >
      {/* Top purple rule — 3 px brand-purple full-width separator between
          the page body and the white footer body. Frames the footer
          without filling it. */}
      <div
        className="w-full"
        style={{ height: 3, background: "var(--pp-purple)" }}
        aria-hidden
        data-testid="shared-footer-top-rule"
      />

      <div className="pp-wrap">
        {/* Inner padding wrapper. The vertical padding lives HERE — not on
            the outer `.pp-wrap` — because `.pp-wrap` declares the CSS
            shorthand `padding: 0 32px` in index.css, which resets
            `padding-top: 0` and silently overrides any Tailwind `pt-*`
            utility on the same element (the shorthand is declared after
            @tailwind utilities; in source order, so it wins the cascade).
            By moving `pt-20` onto a sibling-inner div, the 80 px top gap
            below the purple rule renders correctly. Verified via
            getBoundingClientRect — see footer-spacing verification report. */}
        <div className="pt-10 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {/* COL 1 — brand block.
                
                Feb 2026 footer-overlap fix: the composed lockup used
                to inline `<Logo variant="full">` here — sized `h-20`
                (80px mark) + a 44-pixel wordmark for the
                auth-page hero boxes (128px bounding boxes). Reused
                verbatim in this footer, it rendered ~290px wide with
                a hard `width: max-content` inline style baked into
                Logo.jsx — at the `md:` breakpoint (768–979px) each
                grid column is only ~279px, so the wordmark bled into
                the PRODUCT column. Fix: inline a smaller lockup here
                that mirrors the HEADER's sizing (`h-12` dogmark +
                28px wordmark — see `LogoLockup` in SharedNav.jsx),
                and add `min-w-0` to this column so it can shrink
                inside its grid track without pushing past the gap.
                Logo variant="full" is untouched so the 4 auth-page
                consumers (Login / Signup / GoogleAuthCallback /
                AcceptAdminInvite) still get the hero-sized lockup.
                
                2026-02 follow-up: tagline alignment switched from a
                fixed `pl-[92px]` left-offset to a SHRINK-WRAP parent
                (`w-max`) that constrains both the lockup and the
                tagline to the lockup's exact width. */}
            <div className="min-w-0" data-testid="shared-footer-brand">
              <div
                className="max-w-[240px]"
                data-testid="shared-footer-brand-lockup"
              >
                <Link
                  to="/"
                  className="inline-flex items-center gap-2.5 select-none"
                  aria-label="PooPluckers home"
                  data-testid="shared-footer-logo"
                >
                  <img
                    src="/brand/poopluckers-dogmark.png"
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="pp-dog-mark h-12 w-auto block"
                    data-testid="shared-footer-logo-mark"
                  />
                  <span
                    className="text-[28px] leading-none tracking-[-0.02em]"
                    style={{ fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 800 }}
                    data-testid="shared-footer-logo-wordmark"
                  >
                    <span style={{ color: "var(--pp-orange)" }}>Poo</span>
                    <span style={{ color: "var(--pp-slate)" }}>Pluckers</span>
                  </span>
                </Link>
                <p
                  className="mt-4 text-[15px] max-w-[22em] leading-relaxed"
                  style={{ color: "var(--pp-text-soft)" }}
                  data-testid="shared-footer-tagline"
                >
                  Spotless yards, happy dogs. PooPluckers connects local dog owners
                  with local PooPartners who handle the dirty work. 🐾
                </p>
              </div>
            </div>

            {/* COL 2 — Product */}
            <div data-testid="shared-footer-product">
              <h3
                className="text-[13px] font-bold uppercase tracking-[0.08em] mb-4"
                style={{ color: "var(--pp-slate)" }}
              >
                Product
              </h3>
              <ul className="list-none m-0 p-0 flex flex-col gap-3">
                {PRODUCT_LINKS.map(({ label, to }) => (
                  <li key={label}>
                    <FooterLink
                      to={to}
                      label={label}
                      testId={`shared-footer-product-${label
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    />
                  </li>
                ))}
              </ul>
            </div>

            {/* COL 3 — Legal */}
            <div data-testid="shared-footer-legal">
              <h3
                className="text-[13px] font-bold uppercase tracking-[0.08em] mb-4"
                style={{ color: "var(--pp-slate)" }}
              >
                Legal
              </h3>
              <ul className="list-none m-0 p-0 flex flex-col gap-3">
                {LEGAL_LINKS.map(({ label, to }) => (
                  <li key={label}>
                    <FooterLink
                      to={to}
                      label={label}
                      testId={`shared-footer-legal-${label.toLowerCase()}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Darker purple bottom bar — copyright. White on --pp-purple-dark
          (#473C75) measures ≈ 8.4 : 1 → WCAG AAA. */}
      <div
        className="w-full text-white"
        style={{ background: "var(--pp-purple-dark)" }}
        data-testid="shared-footer-bottom-stripe"
      >
        <div
          className="pp-wrap flex flex-col md:flex-row items-center md:justify-between gap-2 text-center md:text-left"
          style={{ paddingTop: 14, paddingBottom: 14 }}
        >
          <p
            className="text-[13.5px] m-0 text-white"
            data-testid="shared-footer-copyright"
          >
            © {year} PooPluckers · PooPluckers.com — Local dog waste removal.
          </p>
        </div>
      </div>
    </footer>
  );
}
